
<template>
  <div class="users-page">
    <!-- 顶部导航 -->
    <header class="header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span class="header-title">{{ $t('admin.Users.line8_text') }}</span>
      </div>
      <button class="header-icon" @click="handleLogout">🚪</button>
    </header>

    <!-- 搜索栏 -->
    <div class="search-section">
      <div class="search-input-wrapper">
        <input
          v-model="searchQuery"
          :placeholder="$t('admin.Users.line18_placeholder')"
          class="search-input"
          @keyup.enter="handleSearch"
          @input="onSearchInput"
          @focus="showSuggestions = true"
        />
        <button class="search-btn" @click="handleSearch">{{ $t('common.search') }}</button>
        <!-- 搜索建议下拉 -->
        <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-dropdown">
          <div
            v-for="(item, idx) in suggestions"
            :key="idx"
            class="suggestion-item"
            @mousedown.prevent="selectSuggestion(item)"
          >
            <span :class="['suggestion-icon', 'icon-' + item.type]">
              {{ item.type === 'wholesaler' ? '🏢' : item.type === 'stall' ? '🏪' : '👤' }}
            </span>
            <span class="suggestion-text">{{ item.value }}</span>
            <span class="suggestion-type">{{ getTypeLabel(item.type) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <div class="loading-text">{{ $t('common.loading') }}</div>
    </div>

    <!-- 用户列表 -->
    <div v-else class="user-list">
      <div
        v-for="user in userList"
        :key="user.id"
        class="user-item"
      >
        <div class="user-avatar">👤</div>
        <div class="user-info">
          <div class="user-name">{{ user.username }}</div>
          <div class="user-phone">{{ user.phone || '未设置手机号' }}</div>
          <div class="user-roles">
            <span
              v-for="role in user.roles"
              :key="role.id"
              :class="['role-tag', getRoleClass(role.code)]"
            >
              {{ role.name }}
            </span>
            <span class="role-tag role-add" @click="openRoleModal(user)">{{ $t('admin.Users.line68_text') }}</span>
          </div>
        </div>
        <div class="user-actions">
          <button class="action-btn merchants" @click="openMerchantModal(user)">{{ $t('admin.Users.line72_text') }}</button>
          <button class="action-btn edit" @click="openEditModal(user)">{{ $t('common.edit') }}</button>
          <button class="action-btn password" @click="openPasswordModal(user)">{{ $t('admin.Users.line74_text') }}</button>
          <button class="action-btn delete" @click="confirmDelete(user)">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div
      v-if="!loading && userList.length === 0"
      class="empty-state"
    >
      <div class="empty-icon">📭</div>
      <div class="empty-text">
        {{ searchQuery ? '未找到匹配的用户' : '暂无用户数据' }}
      </div>
      <button
        v-if="searchQuery"
        class="reset-btn"
        @click="resetSearch"
      >
        清除搜索
      </button>
    </div>

    <!-- 分页 -->
    <div v-if="totalUsers > 0" class="pagination-wrap">
      <button :disabled="currentPage <= 0" @click="goPage(currentPage - 1)">{{ $t('common.prevPage') }}</button>
      <span class="page-info">{{ currentPage + 1 }} / {{ totalPages }}</span>
      <button :disabled="currentPage >{{ $t('admin.Users.line102_text') }}</button>
      <span class="page-total">共 {{ totalUsers }} 条</span>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <div class="nav-item" @click="goToPage('/admin/dashboard')">
        <div class="nav-icon">🏠</div>
        <div class="nav-label">{{ $t('common.home') }}</div>
      </div>
      <div class="nav-item active" @click="goToPage('/admin/users')">
        <div class="nav-icon">👥</div>
        <div class="nav-label">{{ $t('admin.Users.line114_text') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/roles')">
        <div class="nav-icon">🔐</div>
        <div class="nav-label">{{ $t('admin.Users.line118_text') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/features')">
        <div class="nav-icon">⚙️</div>
        <div class="nav-label">{{ $t('admin.Users.line122_text') }}</div>
      </div>
    </nav>

    <!-- 底部加号按钮 -->
    <button class="fab-btn" @click="openAddModal">➕</button>

    <!-- 新增用户弹窗 -->
    <div
      v-if="showAddModal"
      class="modal-overlay-global"
      @click="closeAddModal"
    >
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('admin.Users.line137_text') }}</h3>
          <button class="modal-close" @click="closeAddModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('admin.Users.line142_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.username"
              type="text"
              :placeholder="$t('admin.Users.line146_placeholder')"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Users.line151_text') }}</label>
            <input
              v-model="formData.phone"
              type="tel"
              :placeholder="$t('admin.Users.line155_placeholder')"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Users.line160_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.password"
              type="password"
              :placeholder="$t('admin.Users.line164_placeholder')"
              class="form-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeAddModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-primary"
            @click="handleAdd"
            :disabled="submitting"
          >
            {{ submitting ? '提交中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑用户弹窗 -->
    <div
      v-if="showEditModal"
      class="modal-overlay-global"
      @click="closeEditModal"
    >
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('admin.Users.line190_text') }}</h3>
          <button class="modal-close" @click="closeEditModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('admin.Users.line195_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.username"
              type="text"
              :placeholder="$t('admin.Users.line199_placeholder')"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Users.line204_text') }}</label>
            <input
              v-model="formData.phone"
              type="tel"
              :placeholder="$t('admin.Users.line208_placeholder')"
              class="form-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeEditModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-primary"
            @click="handleEdit"
            :disabled="submitting"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 角色分配弹窗 -->
    <div
      v-if="showRoleModal"
      class="modal-overlay-global"
      @click="closeRoleModal"
    >
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>分配角色 - {{ currentUser ? currentUser.username : '' }}</h3>
          <button class="modal-close" @click="closeRoleModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="allRoles.length === 0" class="empty-roles">
            <div class="empty-icon">📭</div>
            <div class="empty-text">{{ $t('admin.Users.line240_text') }}</div>
          </div>
          <div v-else class="role-options">
            <div
              v-for="role in allRoles"
              :key="role.id"
              :class="['role-option', { active: isRoleSelected(role) }]"
              @click="toggleRole(role)"
            >
              <span class="role-option-icon">{{ getRoleIcon(role.code) }}</span>
              <span class="role-option-name">{{ role.name }}</span>
              <span class="role-option-desc">{{ role.description || '' }}</span>
              <span
                v-if="isRoleSelected(role)"
                class="role-option-check"
              >✓</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeRoleModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-primary"
            @click="handleSaveRoles"
            :disabled="submitting"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay-global"
      @click="closeDeleteModal"
    >
      <div class="modal modal-sm" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('common.confirmDelete') }}</h3>
          <button class="modal-close" @click="closeDeleteModal">×</button>
        </div>
        <div class="modal-body">
          <p class="delete-confirm-text">
            确定要删除用户
            <strong>{{ currentUser ? currentUser.username : '' }}</strong>
            吗？<br />
            此操作不可恢复。
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeDeleteModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-danger"
            @click="handleDelete"
            :disabled="submitting"
          >
            {{ submitting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 修改密码弹窗 -->
    <div
      v-if="showPasswordModal"
      class="modal-overlay-global"
      @click="closePasswordModal"
    >
      <div class="modal modal-sm" @click.stop>
        <div class="modal-header">
          <h3>修改密码 - {{ currentUser ? currentUser.username : '' }}</h3>
          <button class="modal-close" @click="closePasswordModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('admin.Users.line317_text') }} <span class="required">*</span></label>
            <input
              v-model="passwordForm.newPassword"
              type="password"
              :placeholder="$t('admin.Users.line321_placeholder')"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Users.line326_text') }} <span class="required">*</span></label>
            <input
              v-model="passwordForm.confirmPassword"
              type="password"
              :placeholder="$t('admin.Users.line330_placeholder')"
              class="form-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closePasswordModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-primary"
            @click="handleChangePassword"
            :disabled="submitting"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 关联商户管理弹窗 -->
    <div
      v-if="showMerchantModal"
      class="modal-overlay-global"
      @click="closeMerchantModal"
    >
      <div class="modal modal-merchant" @click.stop>
        <div class="modal-header">
          <h3>关联商户 - {{ currentUser ? currentUser.username : '' }}</h3>
          <button class="modal-close" @click="closeMerchantModal">×</button>
        </div>
        <div class="modal-body">
          <!-- 新增关联按钮 + 搜索 -->
          <div class="merchant-search-bar">
            <input
              v-model="merchantSearchQuery"
              :placeholder="$t('admin.Users.line364_placeholder')"
              class="merchant-search-input"
              @keyup.enter="searchMerchants"
            />
            <button class="merchant-search-btn" @click="searchMerchants">{{ $t('admin.Users.line368_text') }}</button>
            <button class="merchant-search-btn merchant-search-type" @click="toggleSearchType">
              {{ merchantSearchType === 'wholesaler' ? '批发商' : '档口' }}
            </button>
          </div>

          <!-- 搜索结果 -->
          <div v-if="searchResults.length > 0" class="merchant-search-results">
            <div
              v-for="item in searchResults"
              :key="'search-' + item.id"
              class="merchant-item merchant-search-item"
            >
              <div class="merchant-info">
                <div class="merchant-header">
                  <span :class="['merchant-type-tag', item.type === 'wholesaler' ? 'tag-wholesaler' : 'tag-stall']">
                    {{ item.typeLabel }}
                  </span>
                  <span class="merchant-code">{{ item.code }}</span>
                </div>
                <div class="merchant-name">{{ item.name }}</div>
                <div class="merchant-contact" v-if="item.contact || item.phone">
                  <span v-if="item.contact">👤 {{ item.contact }}</span>
                  <span v-if="item.phone" class="merchant-phone">📞 {{ item.phone }}</span>
                </div>
              </div>
              <button class="merchant-link-btn" @click="linkMerchant(item)">{{ $t('admin.Users.line394_text') }}</button>
            </div>
          </div>

          <div v-if="searched && searchResults.length === 0" class="empty-merchants">
            <div class="empty-icon">🔍</div>
            <div class="empty-text">未找到匹配的{{ merchantSearchType === 'wholesaler' ? '批发商' : '档口' }}</div>
          </div>

          <div class="merchant-divider">{{ $t('admin.Users.line403_text') }}</div>

          <!-- 加载中 -->
          <div v-if="merchantLoading" class="empty-merchants">
            <div class="loading-spinner">⏳</div>
            <div class="loading-text">{{ $t('common.loading') }}</div>
          </div>
          <!-- 无关联商户 -->
          <div v-else-if="merchantList.length === 0" class="empty-merchants">
            <div class="empty-icon">📭</div>
            <div class="empty-text">{{ $t('admin.Users.line413_text') }}</div>
          </div>
          <!-- 商户列表 -->
          <div v-else class="merchant-list">
            <div
              v-for="merchant in merchantList"
              :key="merchant.type + '-' + merchant.merchantId + '-' + (merchant.driverId || '')"
              class="merchant-item"
            >
              <div class="merchant-info">
                <div class="merchant-header">
                  <span :class="['merchant-type-tag', merchant.type === 'wholesaler' ? 'tag-wholesaler' : 'tag-stall']">
                    {{ merchant.typeLabel }}
                  </span>
                  <span v-if="merchant.driverId" class="driver-badge">{{ $t('admin.Users.line427_text') }}</span>
                  <span class="merchant-code">{{ merchant.merchantCode }}</span>
                </div>
                <div class="merchant-name">{{ merchant.merchantName }}</div>
                <div class="merchant-contact" v-if="merchant.contactPerson || merchant.contactPhone">
                  <span v-if="merchant.contactPerson">👤 {{ merchant.contactPerson }}</span>
                  <span v-if="merchant.contactPhone" class="merchant-phone">📞 {{ merchant.contactPhone }}</span>
                </div>
              </div>
              <button class="merchant-delete" @click="confirmDeleteMerchant(merchant)">{{ $t('common.delete') }}</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" @click="closeMerchantModal">{{ $t('admin.Users.line441_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 删除商户确认弹窗 -->
    <div
      v-if="showDeleteMerchantModal"
      class="modal-overlay-global"
      @click="closeDeleteMerchantModal"
    >
      <div class="modal modal-sm" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('common.confirmDelete') }}</h3>
          <button class="modal-close" @click="closeDeleteMerchantModal">×</button>
        </div>
        <div class="modal-body">
          <p class="delete-confirm-text">
            确定要删除
            <strong>{{ targetMerchant ? targetMerchant.merchantName : '' }}</strong>
            的关联吗？
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeDeleteMerchantModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-danger"
            @click="handleDeleteMerchant"
            :disabled="submitting"
          >
            {{ submitting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { logout } from '@/utils/auth';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  changePassword,
  assignRoles,
  getRoles,
  getUserDetail,
  linkUserWholesaler,
  getWholesalers,
  getCustomers,
  linkUserCustomer,
  getUserMerchants,
  unlinkUserWholesaler,
  unlinkUserCustomer
} from '@/services/api';
import api from '@/services/api';

export default {
  name: 'UserManagement',
  data() {
    return {
      searchQuery: '',
      loading: false,
      submitting: false,

      showAddModal: false,
      showEditModal: false,
      showRoleModal: false,
      showDeleteModal: false,
      showPasswordModal: false,
      showMerchantModal: false,
      showDeleteMerchantModal: false,

      // 商户管理
      merchantList: [],
      merchantLoading: false,
      targetMerchant: null,
      merchantSearchQuery: '',
      merchantSearchType: 'wholesaler',
      searchResults: [],
      searched: false,
      merchantLinking: false,

      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      },

      formData: {
        username: '',
        phone: '',
        password: ''
      },

      userList: [],
      allRoles: [],
      totalUsers: 0,
      totalPages: 0,
      currentPage: 0,
      pageSize: 20,

      currentUser: null,
      selectedRoleIds: [],

      // 搜索建议
      suggestions: [],
      showSuggestions: false,
      suggestionDebounce: null
    };
  },
  computed: {
    ...mapGetters(['userName']),
  },
  async mounted() {
    await this.loadData();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async handleLogout() {
      await logout();
    },

    async loadData(page = 0) {
      this.loading = true;
      this.currentPage = page;
      try {
        const [usersRes, rolesRes] = await Promise.all([
          getUsers({ page: page, size: this.pageSize, keyword: this.searchQuery || undefined }),
          getRoles()
        ]);
        this.userList = usersRes?.content || [];
        this.totalUsers = usersRes?.totalElements || 0;
        this.totalPages = usersRes?.totalPages || 0;
        this.allRoles = rolesRes || [];
      } catch (error) {
        console.error('加载数据失败:', error);
        this.showToast(error.message || '加载失败', 'error');
      } finally {
        this.loading = false;
      }
    },

    goPage(page) {
      this.loadData(page);
    },

    handleSearch() {
      this.showSuggestions = false;
      this.loadData(0);
    },

    resetSearch() {
      this.searchQuery = '';
      this.showSuggestions = false;
      this.loadData(0);
    },

    // 搜索建议
    onSearchInput() {
      clearTimeout(this.suggestionDebounce);
      const keyword = this.searchQuery.trim();
      if (!keyword) {
        this.suggestions = [];
        this.showSuggestions = false;
        return;
      }
      this.suggestionDebounce = setTimeout(async () => {
        try {
          const res = await api.get('/admin/users/search/suggestions', { params: { keyword } });
          this.suggestions = res || [];
          this.showSuggestions = this.suggestions.length > 0;
        } catch (e) {
          console.error('搜索建议失败:', e);
          this.suggestions = [];
        }
      }, 300);
    },

    selectSuggestion(item) {
      this.searchQuery = item.value;
      this.showSuggestions = false;
      this.handleSearch();
    },

    getTypeLabel(type) {
      const map = { user: '用户', wholesaler: '批发商', stall: '档口' };
      return map[type] || type;
    },

    // 点击外部关闭下拉
    handleClickOutside(e) {
      const wrapper = this.$el.querySelector('.search-input-wrapper');
      if (wrapper && !wrapper.contains(e.target)) {
        this.showSuggestions = false;
      }
    },

    openAddModal() {
      this.formData = {
        username: '',
        phone: '',
        password: ''
      };
      this.showAddModal = true;
    },

    closeAddModal() {
      this.showAddModal = false;
    },

    async handleAdd() {
      if (!this.formData.username) {
        this.showToast('请输入用户名', 'error');
        return;
      }
      if (!this.formData.password) {
        this.showToast('请输入密码', 'error');
        return;
      }

      this.submitting = true;
      try {
        await createUser({
          username: this.formData.username,
          phone: this.formData.phone || '',
          password: this.formData.password
        });
        this.showToast('添加成功', 'success');
        this.closeAddModal();
        await this.loadData();
      } catch (error) {
        console.error('添加用户失败:', error);
        this.showToast(error.message || '添加失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    openEditModal(user) {
      this.currentUser = user;
      this.formData = {
        username: user.username,
        phone: user.phone || '',
        password: ''
      };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
    },

    async handleEdit() {
      if (!this.formData.username) {
        this.showToast('请输入用户名', 'error');
        return;
      }

      this.submitting = true;
      try {
        await updateUser(this.currentUser.id, {
          username: this.formData.username,
          phone: this.formData.phone || ''
        });
        this.showToast('保存成功', 'success');
        this.closeEditModal();
        await this.loadData();
      } catch (error) {
        console.error('编辑用户失败:', error);
        this.showToast(error.message || '保存失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    confirmDelete(user) {
      this.currentUser = user;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
    },

    openPasswordModal(user) {
      this.currentUser = user;
      this.passwordForm = {
        newPassword: '',
        confirmPassword: ''
      };
      this.showPasswordModal = true;
    },

    closePasswordModal() {
      this.showPasswordModal = false;
      this.passwordForm = {
        newPassword: '',
        confirmPassword: ''
      };
    },

    async handleChangePassword() {
      if (!this.passwordForm.newPassword) {
        this.showToast('请输入新密码', 'error');
        return;
      }
      if (this.passwordForm.newPassword.length < 6) {
        this.showToast('密码至少6位', 'error');
        return;
      }
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.showToast('两次密码不一致', 'error');
        return;
      }

      this.submitting = true;
      try {
        await changePassword(this.currentUser.id, {
          newPassword: this.passwordForm.newPassword
        });
        this.showToast('密码修改成功', 'success');
        this.closePasswordModal();
      } catch (error) {
        console.error('修改密码失败:', error);
        this.showToast(error.message || '修改失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    async handleDelete() {
      this.submitting = true;
      try {
        await deleteUser(this.currentUser.id);
        this.showToast('删除成功', 'success');
        this.closeDeleteModal();
        await this.loadData();
      } catch (error) {
        console.error('删除用户失败:', error);
        this.showToast(error.message || '删除失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    async openRoleModal(user) {
      this.currentUser = user;
      this.selectedRoleIds = (user.roles || []).map(r => r.id);

      if (this.allRoles.length === 0) {
        try {
          const rolesRes = await getRoles();
          this.allRoles = rolesRes || [];
        } catch (error) {
          console.error('加载角色失败:', error);
          this.showToast('加载角色失败：' + error.message, 'error');
        }
      }

      this.showRoleModal = true;
    },

    closeRoleModal() {
      this.showRoleModal = false;
    },

    isRoleSelected(role) {
      return this.selectedRoleIds.includes(role.id);
    },

    toggleRole(role) {
      const index = this.selectedRoleIds.indexOf(role.id);
      if (index > -1) {
        this.selectedRoleIds.splice(index, 1);
      } else {
        this.selectedRoleIds.push(role.id);
      }
    },

    async handleSaveRoles() {
      this.submitting = true;
      try {
        // 检查是否分配了批发商角色
        const wholesalerRole = this.allRoles.find(r => r.code === 'WHOLESALER');
        const isAssigningWholesaler = wholesalerRole && this.selectedRoleIds.includes(wholesalerRole.id);
        
        // 检查是否分配了档口角色
        const stallRole = this.allRoles.find(r => r.code === 'STALL');
        const isAssigningStall = stallRole && this.selectedRoleIds.includes(stallRole.id);
        
        // 如果分配批发商角色，检查是否已关联批发商
        if (isAssigningWholesaler) {
          const userDetail = await getUserDetail(this.currentUser.id);
          if (!userDetail.wholesalerId) {
            // 未关联批发商，弹出选择框
            const wholesalerId = await this.showWholesalerSelect();
            if (!wholesalerId) {
              this.showToast('已取消', 'error');
              this.submitting = false;
              return;
            }
            // 关联批发商
            await linkUserWholesaler(this.currentUser.id, wholesalerId);
          }
        }
        
        // 如果分配档口角色，检查是否已关联档口
        if (isAssigningStall) {
          const userDetail = await getUserDetail(this.currentUser.id);
          if (!userDetail.customerId || !userDetail.stallId) {
            // 未关联档口，弹出选择框
            const stallId = await this.showStallSelect();
            if (!stallId) {
              this.showToast('已取消', 'error');
              this.submitting = false;
              return;
            }
            // 关联档口
            await linkUserCustomer(this.currentUser.id, stallId);
          }
        }
        
        // 分配角色
        await assignRoles(this.currentUser.id, this.selectedRoleIds);
        this.showToast('角色分配成功', 'success');
        this.closeRoleModal();
        await this.loadData();
      } catch (error) {
        console.error('分配角色失败:', error);
        this.showToast(error.message || '分配失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    async showWholesalerSelect() {
      const response = await getWholesalers();
      const wholesalers = Array.isArray(response) ? response : (response.content || []);
      
      if (!wholesalers || wholesalers.length === 0) {
        this.showToast('暂无批发商，请先创建', 'error');
        return null;
      }
      
      // 使用自定义对话框（带搜索）
      return new Promise((resolve) => {
        const dialog = document.createElement('div');
        dialog.style.cssText = `
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
        `;
        
        // 生成批发商列表 HTML
        const listHtml = wholesalers.map(w => `
          <div class="wholesaler-item" data-id="${w.id}" style="
            padding: 12px 16px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
            transition: background 0.2s;
          " onmouseover="this.style.background='#f0f2ff'" onmouseout="this.style.background='white'">
            <div style="font-weight: 600; color: #333;">${w.companyName || '未命名'}</div>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">ID: ${w.id} ${w.address ? '· ' + w.address : ''}</div>
          </div>
        `).join('');
        
        dialog.innerHTML = `
          <div style="
            background: white;
            padding: 24px;
            border-radius: 12px;
            min-width: 360px;
            max-width: 420px;
            max-height: 80vh;
            box-shadow: 0 10px 40px rgba(0,0,0,0.2);
            display: flex;
            flex-direction: column;
          ">
            <h3 style="margin: 0 0 16px 0; font-size: 18px;">选择批发商</h3>
            
            <!-- 搜索框 -->
            <input 
              id="searchInput" 
              placeholder="搜索批发商名称或 ID..." 
              style="
                width: 100%;
                padding: 12px;
                font-size: 14px;
                border: 1px solid #ddd;
                border-radius: 6px;
                margin-bottom: 16px;
                box-sizing: border-box;
              "
              oninput="filterWholesalers(this.value)"
            />
            
            <!-- 批发商列表 -->
            <div id="wholesalerList" style="
              flex: 1;
              overflow-y: auto;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              margin-bottom: 20px;
              max-height: 300px;
            ">
              ${listHtml}
            </div>
            
            <!-- 按钮 -->
            <div style="display: flex; gap: 12px; justify-content: flex-end;">
              <button id="cancelBtn" style="
                padding: 10px 24px;
                border: 1px solid #ddd;
                background: white;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
              ">取消</button>
            </div>
          </div>
        `;
        
        document.body.appendChild(dialog);
        
        // 搜索功能
        window.filterWholesalers = (query) => {
          const items = dialog.querySelectorAll('.wholesaler-item');
          const q = query.toLowerCase();
          items.forEach(item => {
            const text = item.textContent.toLowerCase();
            item.style.display = text.includes(q) ? 'block' : 'none';
          });
        };
        
        // 点击选择
        dialog.querySelectorAll('.wholesaler-item').forEach(item => {
          item.onclick = () => {
            const selectedId = parseInt(item.getAttribute('data-id'));
            document.body.removeChild(dialog);
            resolve(selectedId);
          };
        });
        
        // 取消
        document.getElementById('cancelBtn').onclick = () => {
          document.body.removeChild(dialog);
          resolve(null);
        };
      });
    },

    async showStallSelect() {
      const response = await getCustomers({ pageNo: 0, pageSize: 1000 });
      const stalls = Array.isArray(response) ? response : (response.content || []);
      
      if (!stalls || stalls.length === 0) {
        this.showToast('暂无档口，请先创建', 'error');
        return null;
      }
      
      // 使用自定义对话框（带搜索）
      return new Promise((resolve) => {
        const dialog = document.createElement('div');
        dialog.style.cssText = `
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
        `;
        
        // 生成档口列表 HTML
        const listHtml = stalls.map(s => `
          <div class="stall-item" data-id="${s.id}" style="
            padding: 12px 16px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
            transition: background 0.2s;
          " onmouseover="this.style.background='#fff7e6'" onmouseout="this.style.background='white'">
            <div style="font-weight: 600; color: #333;">${s.customerName || s.customerName || '未命名'}</div>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">ID: ${s.id} ${s.address ? '· ' + s.address : ''} ${s.stallNumber ? '· ' + s.stallNumber : ''}</div>
          </div>
        `).join('');
        
        dialog.innerHTML = `
          <div style="
            background: white;
            padding: 24px;
            border-radius: 12px;
            min-width: 360px;
            max-width: 420px;
            max-height: 80vh;
            box-shadow: 0 10px 40px rgba(0,0,0,0.2);
            display: flex;
            flex-direction: column;
          ">
            <h3 style="margin: 0 0 16px 0; font-size: 18px;">选择档口</h3>
            
            <!-- 搜索框 -->
            <input 
              id="searchInput" 
              placeholder="搜索档口名称、地址或 ID..." 
              style="
                width: 100%;
                padding: 12px;
                font-size: 14px;
                border: 1px solid #ddd;
                border-radius: 6px;
                margin-bottom: 16px;
                box-sizing: border-box;
              "
              oninput="filterStalls(this.value)"
            />
            
            <!-- 档口列表 -->
            <div id="stallList" style="
              flex: 1;
              overflow-y: auto;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              margin-bottom: 20px;
              max-height: 300px;
            ">
              ${listHtml}
            </div>
            
            <!-- 按钮 -->
            <div style="display: flex; gap: 12px; justify-content: flex-end;">
              <button id="cancelBtn" style="
                padding: 10px 24px;
                border: 1px solid #ddd;
                background: white;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
              ">取消</button>
            </div>
          </div>
        `;
        
        document.body.appendChild(dialog);
        
        // 搜索功能
        window.filterStalls = (query) => {
          const items = dialog.querySelectorAll('.stall-item');
          const q = query.toLowerCase();
          items.forEach(item => {
            const text = item.textContent.toLowerCase();
            item.style.display = text.includes(q) ? 'block' : 'none';
          });
        };
        
        // 点击选择
        dialog.querySelectorAll('.stall-item').forEach(item => {
          item.onclick = () => {
            const selectedId = parseInt(item.getAttribute('data-id'));
            document.body.removeChild(dialog);
            resolve(selectedId);
          };
        });
        
        // 取消
        document.getElementById('cancelBtn').onclick = () => {
          document.body.removeChild(dialog);
          resolve(null);
        };
      });
    },

    getRoleIcon(roleCode) {
      const iconMap = {
        ADMIN: '👑',
        STALL: '🏪',
        WHOLESALER: '📦',
        DRIVER: '🚚'
      };
      return iconMap[roleCode] || '👤';
    },

    getRoleClass(roleCode) {
      const classMap = {
        ADMIN: 'role-admin',
        STALL: 'role-stall',
        WHOLESALER: 'role-wholesaler',
        DRIVER: 'role-driver'
      };
      return classMap[roleCode] || '';
    },

    // ==================== 商户管理 ====================

    async openMerchantModal(user) {
      this.currentUser = user;
      this.merchantList = [];
      this.merchantLoading = true;
      this.showMerchantModal = true;

      try {
        const merchants = await getUserMerchants(user.id);
        this.merchantList = merchants || [];
      } catch (error) {
        console.error('加载商户列表失败:', error);
        this.showToast('加载失败：' + error.message, 'error');
      } finally {
        this.merchantLoading = false;
      }
    },

    closeMerchantModal() {
      this.showMerchantModal = false;
    },

    confirmDeleteMerchant(merchant) {
      this.targetMerchant = merchant;
      this.showDeleteMerchantModal = true;
    },

    closeDeleteMerchantModal() {
      this.showDeleteMerchantModal = false;
      this.targetMerchant = null;
    },

    async handleDeleteMerchant() {
      if (!this.targetMerchant || !this.currentUser) return;

      this.submitting = true;
      try {
        const { merchantId, type, driverId } = this.targetMerchant;
        if (type === 'wholesaler' && !driverId) {
          await unlinkUserWholesaler(this.currentUser.id, merchantId);
        } else if (type === 'stall') {
          await unlinkUserCustomer(this.currentUser.id, merchantId);
        } else if (type === 'wholesaler' && driverId) {
          // 配送员关联的批发商
          await api.delete(`/admin/users/${this.currentUser.id}/merchants/driver/${driverId}`);
        }
        this.showToast('删除成功', 'success');
        this.closeDeleteMerchantModal();
        // 重新加载商户列表
        this.merchantList = this.merchantList.filter(m =>
          !(m.type === this.targetMerchant.type && m.merchantId === merchantId)
        );
      } catch (error) {
        console.error('删除商户关联失败:', error);
        this.showToast(error.message || '删除失败', 'error');
      } finally {
        this.submitting = false;
      }
    },

    // ==================== 新增关联 ====================

    toggleSearchType() {
      this.merchantSearchType = this.merchantSearchType === 'wholesaler' ? 'stall' : 'wholesaler';
      this.searchResults = [];
      this.searched = false;
      this.merchantSearchQuery = '';
    },

    async searchMerchants() {
      if (!this.merchantSearchQuery.trim()) {
        this.showToast('请输入搜索关键词', 'error');
        return;
      }
      this.searched = true;
      this.searchResults = [];
      try {
        const endpoint = this.merchantSearchType === 'wholesaler' ? '/admin/users/search/wholesalers' : '/admin/users/search/customers';
        const res = await api.get(endpoint, { params: { keyword: this.merchantSearchQuery } });
        const items = res.data || res || [];
        const linkedIds = new Set(this.merchantList.map(m => m.merchantId));
        this.searchResults = items
          .filter(item => !linkedIds.has(item.id))
          .map(item => ({
            id: item.id,
            code: item.code,
            name: item.name,
            contact: item.contact,
            phone: item.phone,
            type: this.merchantSearchType === 'wholesaler' ? 'wholesaler' : 'stall',
            typeLabel: this.merchantSearchType === 'wholesaler' ? '批发商' : '档口'
          }));
      } catch (error) {
        console.error('搜索失败:', error);
        this.showToast(error.message || '搜索失败', 'error');
      }
    },

    async linkMerchant(item) {
      this.merchantLinking = true;
      try {
        if (item.type === 'wholesaler') {
          await api.post(`/admin/users/${this.currentUser.id}/merchants/wholesaler`, null, {
            params: { wholesalerId: item.id }
          });
        } else {
          await api.post(`/admin/users/${this.currentUser.id}/merchants/customer`, null, {
            params: { customerId: item.id }
          });
        }
        this.showToast('关联成功', 'success');
        // Refresh merchant list
        const merchants = await getUserMerchants(this.currentUser.id);
        this.merchantList = merchants || [];
        // Clear search
        this.searchResults = [];
        this.searched = false;
        this.merchantSearchQuery = '';
      } catch (error) {
        console.error('关联失败:', error);
        this.showToast(error.message || '关联失败', 'error');
      } finally {
        this.merchantLinking = false;
      }
    },

    goBack() {
      this.$router.back();
    },

    goToPage(path) {
      if (this.$route.path === path) return;
      this.$router.push(path).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err);
        }
      });
    },

    showToast(message, type = 'info') {
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: ${
          type === 'error'
            ? 'rgba(255,77,79,0.9)'
            : type === 'success'
            ? 'rgba(52,199,89,0.9)'
            : 'rgba(0,0,0,0.7)'
        };
        color: #fff;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        z-index: 9999;
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
    }
  }
};
</script>

<style scoped>
.modal-overlay-global {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0,0,0,0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 9999 !important;
}

.users-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.back-icon {
  font-size: 20px;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.header-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
}

.search-section {
  background: #fff;
  padding: 12px 16px;
  display: flex;
  gap: 8px;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.search-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

/* 搜索建议下拉 */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
  margin-top: 2px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background: #f0f4ff;
}

.suggestion-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.suggestion-text {
  flex: 1;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-type {
  font-size: 11px;
  color: #999;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.loading-state {
  text-align: center;
  padding: 60px 16px;
}

.loading-spinner {
  font-size: 48px;
  margin-bottom: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #999;
}

.user-list {
  padding: 12px;
}

.user-item {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.user-avatar {
  width: 48px;
  height: 48px;
  background: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.user-phone {
  font-size: 13px;
  color: #999;
  margin-bottom: 6px;
}

.user-roles {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.role-tag {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.role-admin {
  background: #fff2f0;
  color: #ff4d4f;
}

.role-stall {
  background: #fffbe6;
  color: #faad14;
}

.role-wholesaler {
  background: #e6f4ff;
  color: #1890ff;
}

.role-driver {
  background: #f0f5ff;
  color: #597ef7;
}

.role-add {
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
}

.user-actions {
  display: flex;
  gap: 8px;
  flex-direction: column;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.action-btn.edit {
  background: #f0f0f0;
  color: #666;
}

.action-btn.delete {
  background: #fff2f0;
  color: #ff4d4f;
}

.action-btn.password {
  background: #e6f7ff;
  color: #1890ff;
}

.action-btn.merchants {
  background: #f6ffed;
  color: #52c41a;
}

.empty-state {
  text-align: center;
  padding: 60px 16px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #999;
  margin-bottom: 16px;
}

.reset-btn {
  padding: 8px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  display: flex;
  border-top: 1px solid #e0e0e0;
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  cursor: pointer;
}

.nav-item.active {
  color: #667eea;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 2px;
}

.nav-label {
  font-size: 12px;
}

.fab-btn {
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  font-size: 28px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Pagination */
.pagination-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-top: 1px solid #f0f0f0;
}
.pagination-wrap button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
}
.pagination-wrap button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pagination-wrap button:not(:disabled):hover {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}
.page-info {
  font-weight: 600;
  color: #333;
}
.page-total {
  color: #999;
  font-size: 13px;
}

.modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow: auto;
}

.modal-sm {
  max-width: 320px;
}

.modal-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 16px;
}

.modal-footer {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.btn-danger {
  background: #ff4d4f;
  color: #fff;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.role-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.empty-roles {
  text-align: center;
  padding: 20px;
}

.empty-roles .empty-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.empty-roles .empty-text {
  font-size: 14px;
  color: #999;
}

.role-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.role-option:hover {
  background: #e8e8e8;
}

.role-option.active {
  background: #e6f4ff;
  border: 2px solid #1890ff;
}

.role-option-icon {
  font-size: 24px;
}

.role-option-name {
  font-size: 15px;
  font-weight: 500;
}

.role-option-desc {
  flex: 1;
  font-size: 12px;
  color: #999;
}

.role-option-check {
  color: #1890ff;
  font-size: 20px;
  font-weight: bold;
}

.delete-confirm-text {
  font-size: 15px;
  color: #333;
  line-height: 1.6;
}

.delete-confirm-text strong {
  color: #ff4d4f;
}

/* 商户管理弹窗样式 */
.modal-merchant {
  max-width: 450px;
}

.empty-merchants {
  text-align: center;
  padding: 40px 20px;
}

.empty-merchants .empty-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.empty-merchants .empty-text {
  font-size: 14px;
  color: #999;
}

.merchant-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.merchant-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.merchant-info {
  flex: 1;
  min-width: 0;
}

.merchant-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.merchant-type-tag {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.tag-wholesaler {
  background: #e6f4ff;
  color: #1890ff;
}

.tag-stall {
  background: #fffbe6;
  color: #faad14;
}

.driver-badge {
  font-size: 11px;
  padding: 2px 6px;
  background: #f0f0f0;
  border-radius: 4px;
  color: #666;
}

.merchant-code {
  font-size: 12px;
  color: #999;
  font-family: monospace;
}

.merchant-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.merchant-contact {
  font-size: 12px;
  color: #666;
}

.merchant-phone {
  margin-left: 12px;
}

.merchant-delete {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #fff2f0;
  color: #ff4d4f;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.merchant-delete:hover {
  background: #ff4d4f;
  color: white;
}

/* 商户搜索栏 */
.merchant-search-bar {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.merchant-search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
}

.merchant-search-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  white-space: nowrap;
}

.merchant-search-type {
  background: #f0f0f0;
  color: #666;
}

/* 搜索结果 */
.merchant-search-results {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.merchant-search-item {
  background: #f0f7ff;
  border-color: #d0e5ff;
}

.merchant-link-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #52c41a;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.merchant-link-btn:hover {
  background: #389e0d;
}

.merchant-link-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.merchant-divider {
  text-align: center;
  color: #999;
  font-size: 12px;
  margin: 12px 0;
  position: relative;
}

.merchant-divider::before,
.merchant-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 30%;
  height: 1px;
  background: #e0e0e0;
}

.merchant-divider::before { left: 0; }
.merchant-divider::after { right: 0; }
</style>
